import type { UpdateData as UpdateSourceData, WorkerConnection } from '#src/js/WorkerConnection.mts'
import { provideWorkerConnection, useWorkerConnection } from '#src/js/WorkerConnection.mts'
import DataTransformation, { type Transformation } from '#src/js/dataTransformation.mts'
import { mapToSection } from '#src/js/utils.mts'
import type { RoomMeta } from '#src/js/worker/Room.mts'
import { sortBy } from 'lodash/fp'
import type { MaybeRef, Plugin } from 'vue'
import { reactive } from 'vue'

export { UpdateSourceData }
export type WrapperFunction = ((data: UpdateData<any>) => unknown)

export type SectionData<Sections extends any> = {
    [SectionKey in keyof Sections]: {
      list: Sections[SectionKey][]
      byUid: Record<string, Sections[SectionKey]>
      meta: any
    }
}

export type UpdateData<Sections extends any = any, Static extends any = any> = {
  meta: RoomMeta
  sections: Partial<SectionData<Sections>>
  updateId: number
  raw: any
  static: Static
}

const operationDict = {
  csv: (value: string) => value.split(','),
  set: (value: string) => new Set(value),
  parseInt: (value: string) =>
    Array.isArray(value) ? value.map((v) => parseInt(v)) : parseInt(value),
  date: (value: string) => new Date(value),
} as const

const dataTransformations = new DataTransformation(operationDict)

export type SectionMeta = {
  uid_field: number
  hash_field?: number
  'foreign-keys': string[][]
  through: (string | undefined)[][]
  transformations?: Array<Transformation<typeof operationDict, any>>
}

export type RawSection<K extends any> = {
  header: (keyof K)[]
  meta: SectionMeta
  rows: Record<string, K[]>
}

export type Section<K extends any> = {
  meta: Required<RawSection<K>['meta']> & { header: RawSection<K>['header'] }
  byUid: Record<string, K>
  list: K[]
}

export type SectionDict<Sections = any> = {
  [key in keyof Sections]: Section<Sections[key]>
}


// helpers

function formatUUID (uuid: string) {
  if (uuid == null) {
    return null
  }
  if (uuid.search('-') !== -1) {
    return uuid
  }
  let tail = uuid
  const parts: string[] = [];
  [8, 4, 4, 4, 12].forEach((l) => {
    parts.push(tail.substring(0, l))
    tail = tail.substring(l)
  })
  return parts.join('-')
}

function rowsToObjects (section: RawSection<any>) {
  const header = section.header
  const rows = section.rows

  return Object.fromEntries(
    Object.entries(rows).map(([uid, row]) => [
      uid,
      row.reduce((obj, field, index) => {
        obj[header[index]] = field
        return obj
      }, {})
    ])
  )
}

type ThroughType = 'many' | 'single'
type ThroughMetaItemProperties = {
    section: string,
    through_id_field: string,
    target_result_field: string,
    type?: ThroughType,
    target_id_field?: string,
    order_by?: string[] | undefined,
  }


class ThroughMetaItem {
  public section: string
  public through_id_field: string
  public target_result_field: string
  public type: ThroughType = 'many'
  public target_id_field: string
  public order_by: string[] | undefined

  constructor (throughMetaItemProperties: ThroughMetaItemProperties) {
    const { section, through_id_field, target_result_field, type = 'many', target_id_field, order_by } = throughMetaItemProperties
    this.section = section
    this.through_id_field = through_id_field
    this.target_result_field = target_result_field
    this.type = type;
    this.order_by = order_by

    // default to splitting the through_id_field such that 'object__uid' => 'uid'
    this.target_id_field = target_id_field
      ?? ((parts) => (parts[parts.length - 1]))(through_id_field.split('__'))

    this.type = type

    console.assert(this.section !== null, 'Missing through meta field: section')
    console.assert(
      (this.through_id_field !== null),
      'Missing through meta field: through_id_field',
    )

    console.assert(
      ['single', 'many'].includes(this.type),
      'Unknown through meta type',
      this.type,
    )
  }

  static fromData([section, through_id_field, target_result_field, type, target_id_field, order_by, ..._]: unknown[]) {
    return new ThroughMetaItem({
      section: section as string,
      through_id_field: through_id_field as string,
      target_result_field: target_result_field as string,
      type: type as ThroughType | undefined,
      target_id_field: target_id_field as string | undefined,
      order_by: order_by as (string[] | undefined),
    })
  }

  /**
   * Set the target_result_field to the default value for all items in the
   * target section.
   *
   * return false if the target section is not included in the given data
   * otherwise return true
   */
  setDefault (sections: SectionDict) {
    if (!(this.section in sections)) {
      return false
    }

    const section = sections[this.section]
    const result_field = this.target_result_field

    console.assert(
      section.meta.header.includes(this.target_id_field),
      'Through meta target_id_field missing in target section', this, section)

    console.assert(
      !section.meta.header.includes(this.target_result_field),
      'Through meta target_result_field already exists in section headers',
      this, section)

    const _default: ((item: any[]) => void) = this.type === 'single'
      ? (item) => { item[result_field] = null }
      : (item) => { item[result_field] = [] }

    for (const item of section.list) {
      _default(item)
    }

    return true
  }

  getSectionDict (sections: SectionDict) {
    const section = sections[this.section]
    const id_field = this.target_id_field

    return Object.fromEntries(section.list.map(
      (item: any[]) => ([item[id_field], item])))
  }

  getItem (through: any, sectionDict: Section<any>['byUid']) {
    const target_id = through[this.through_id_field]
    const item = sectionDict[target_id]

    return item
  }

  register (item: any, relatedItem: any) {
    const { type, target_result_field } = this
    console.assert(item !== undefined, 'WebsocketUpdate module unexpected null value')

    if (target_result_field === null) {
      return
    }

    if (target_result_field === "roles" && item.uid === "01967eed-70fc-77c3-bbda-3dc29c1bb5df") {
      console.warn("Adding role to roles", item[target_result_field], Object.assign({}, item), Object.assign({}, relatedItem))
    }

    if (type === 'single') {
      item[target_result_field] = relatedItem
    } else {
      item[target_result_field].push(relatedItem)
    }
  }
}

class ThroughMeta {
  public infoLeft: ThroughMetaItem
  public infoRight: ThroughMetaItem
  constructor (
    infoLeft: unknown[],
    infoRight: unknown[],
  ) {
    this.infoLeft = ThroughMetaItem.fromData(infoLeft)
    this.infoRight = ThroughMetaItem.fromData(infoRight)
  }

  apply (throughSection: Section<any>, sections: SectionDict) {
    // console.debug("ThroughMeta applying to", throughSection)
    const { infoLeft, infoRight } = this
    const sanityCheck = [
      infoLeft.setDefault(sections),
      infoRight.setDefault(sections),
    ]

    if (!sanityCheck.every(Boolean)) {
      // Not all routers use all sections, so sometimes meta is irrelevant
      return
    }

    const dicts = {
      left: infoLeft.getSectionDict(sections),
      right: infoRight.getSectionDict(sections),
    }

    const sortedLeft = (infoLeft.order_by !== undefined)
      ? sortBy(infoLeft.order_by)(throughSection.list)
      : throughSection.list

    const sortedRight = (infoRight.order_by !== undefined)
      ? sortBy(infoRight.order_by)(throughSection.list)
      : throughSection.list

    for (const through of sortedLeft) {
      const left = infoLeft.getItem(through, dicts.left)
      const right = infoRight.getItem(through, dicts.right)

      if (left === undefined || right === undefined) {
        continue
      }
      // console.debug("registering through item", left.uid, right.uid)
      infoLeft.register(left, right)
    }

    for (const through of sortedRight) {
      const left = infoLeft.getItem(through, dicts.left)
      const right = infoRight.getItem(through, dicts.right)

      if (left === undefined || right === undefined) {
        continue
      }

      // console.debug("registering through item", right.uid, left.uid)
      infoRight.register(right, left)
    }
  }
}
const foreignKeyTypes = ['one-to-one', 'many-to-one', 'many-to-many', 'forward', 'reverse'] as const
type ForeignKeyType = (typeof foreignKeyTypes)[number]

class ForeignKeyMeta {
  constructor (
    public section: string,
    public forward_id_field: string,
    public reverse_id_field: string,
    public type: ForeignKeyType,
    public order: string | null | undefined,
    public forwardAlias: string | null = null,
    public reverseAlias: string | null = null,
    ..._: any[]
  ) {
    this.section = section
    this.forward_id_field = forward_id_field
    this.reverse_id_field = reverse_id_field
    this.type = type
    this.order = order

    console.assert(section !== null, 'Missing foreign-key meta field: section')

    console.assert(
      foreignKeyTypes.includes(type),
      'Unknown through meta type', type)
  }

  static fromData([section, forward_id_field, reverse_id_field, type, order, forwardAlias, reverseAlias, ..._]: unknown[]) {
    return new ForeignKeyMeta(
      section as string,
      forward_id_field as string,
      reverse_id_field as string,
      type as ForeignKeyType,
      order as string | null | undefined,
      (forwardAlias ?? null) as string | null,
      (reverseAlias ?? null) as string | null,
      )
  }

  apply (sourceSection: Section<any>, sections: SectionDict) {
    // Routers don't always include sections mentioned in meta information
    if (!(this.section in sections)) {
      return
    }

    switch (this.type) {
      case 'one-to-one':
        this.applyOneToOne(sourceSection, sections)
        break
      case 'many-to-one':
        this.applyManyToOne(sourceSection, sections)
        break
      case 'many-to-many':
        this.applyManyToMany(sourceSection, sections)
        break
      case 'forward':
        this.applyForward(sourceSection, sections)
        break
      case 'reverse':
        this.applyReverse(sourceSection, sections)
        break
      default:
        console.error('Unknown foreign key relation type', this.type)
        break
    }
  }

  applyOneToOne (source: Section<any>, sections: SectionDict) {
    const list = source.list
    const targetDict = sections[this.section].byUid
    const attr = this.forward_id_field
    const reverseAttr = this.reverse_id_field

    const forwardField = this.forwardAlias ?? attr
    const reverseField = this.reverseAlias ?? reverseAttr

    if (forwardField === null) {
      console.error("forwardField is null")
    }
    if (reverseField === null) {
      console.error("reverseField is null")
    }

    const uidAttr = attr + '__uid'

    // set forward relation to target or null
    for (const item of list) {
      const uid = formatUUID(item[uidAttr])
      const target = uid ? targetDict[uid] : null
      item[forwardField] = target
    }

    // set non-null reverse relations to value
    for (const item of list) {
      const target = item[attr]
      if (target) {
        target[reverseField] = item
      }
    }

    // set remaining reverse relations to null
    const targetSet = new Set(list.map(item => item[attr]))
    const nullReverseRelations = Object.values(targetDict)
      .filter(item => !targetSet.has(item))

    for (const item of nullReverseRelations) {
      item[reverseAttr] = null
    }
  }

  applyManyToOne (source: Section<any>, sections: SectionDict) {
    const list = source.list
    const targetDict = sections[this.section].byUid
    const attr = this.forward_id_field
    const reverseAttr = this.reverse_id_field
    const uidAttr = attr + '__uid'

    const forwardField = this.forwardAlias ?? attr
    const reverseField = this.reverseAlias ?? reverseAttr

    if (forwardField === null) {
      console.error("forwardField is null")
    }
    if (reverseField === null) {
      console.error("reverseField is null")
    }

    for (const item of list) {
      const uid = formatUUID(item[uidAttr])
      const target = uid ? targetDict[uid] : null
      item[forwardField] = target
    }

    // initialize all reverse relations to empty list
    for (const item of Object.values(targetDict)) {
      item[reverseField] = []
    }

    // add values to initialized lists
    for (const item of list) {
      const target = item[attr]
      if (target) {
        if (reverseField === null) {
          console.error("reverseField is null")
        }
        target[reverseField].push(item)
      }
    }
  }

  applyManyToMany (source: Section<any>, sections: SectionDict) {
    const list = source.list
    const targetDict = sections[this.section].byUid
    const attr = this.forward_id_field
    const reverseAttr = this.reverse_id_field

    const forwardField = this.forwardAlias ?? attr
    const reverseField = this.reverseAlias ?? reverseAttr

    if (forwardField === null) {
      console.error("forwardField is null")
    }
    if (reverseField === null) {
      console.error("reverseField is null")
    }

    const uidAttr = attr + '__uids'

    if (forwardField === null) {
      console.error("forwardField is null")
    }

    for (const item of list) {
      const string = item[uidAttr]
      const uids = (string ? string.split(',') : []).map(formatUUID)
      const targets = uids.map((uid: string) => targetDict[uid])
      item[forwardField] = targets
    }

    for (const item of Object.values(targetDict)) {
      item[reverseField] = []
    }

    for (const item of list) {
      const targets = item[attr]
      if (!targets) {
        continue
      }
      for (const target of targets) {
        if (target) {
          if (reverseField === null) {
            console.error("reverseField is null")
          }
          target[reverseField].push(item)
        }
      }
    }
  }

  applyForward (source: Section<any>, sections: SectionDict) {
    const list = source.list
    const targetDict = sections[this.section].byUid
    const attr = this.forward_id_field

    const forwardField = this.forwardAlias ?? attr

    if (forwardField === null) {
      console.error("forwardField is null")
    }

    if (forwardField === null) {
      console.error("forwardField is null")
    }

    const uidAttr = attr + '__uid'

    // set forward relation to target or null
    for (const item of list) {
      const uid = formatUUID(item[uidAttr])
      const target = uid ? targetDict[uid] : null
      item[forwardField] = target
    }
  }

  applyReverse (source: Section<any>, sections: SectionDict) {
    const list = source.list
    const targetDict = sections[this.section].byUid
    const attr = this.forward_id_field
    const reverseAttr = this.reverse_id_field

    const reverseField = this.reverseAlias ?? reverseAttr

    if (reverseField === null) {
      console.error("reverseField is null")
    }

    const uidAttr = attr + '__uid'
    const targetSet = new Set()

    // set non-null reverse relations to value
    for (const item of list) {
      const uid = formatUUID(item[uidAttr])
      const target = uid ? targetDict[uid] : null
      if (target) {
        target[reverseField] = item
        targetSet.add(target)
      }
    }

    // set remaining reverse relations to null
    Object.values(targetDict)
      .filter(item => !targetSet.has(item))
      .forEach(item => { item[reverseField] = null })
  }
}

class Meta {
  public foreign_keys: ForeignKeyMeta[]
  public through: ThroughMeta | null
  constructor (meta: SectionMeta) {
    /* tslint:disable-next-line */
    this.foreign_keys = ('foreign-keys' in meta)
      ? meta['foreign-keys'].map((item) => (ForeignKeyMeta.fromData(item)))
      : []

    this.through = 'through' in meta
      ? new ThroughMeta(meta.through[0], meta.through[1])
      : null
  }
}

// logic section //

function mapSectionData (routerData: any) {
  /* Transform the raw incoming data (router => section => rows)
   * into section data (section => {byUid, list, meta})
   *
   * TODO Apply fk and transformations before mapping into sections to remove issues with metadata
   *
   * Also applies foreign keys and transformations
   */
  const sections: SectionDict = {}

  mapToSection(routerData, ([_, sk, section]) => {
    if (!(sk in sections)) {
      sections[sk] = {
        byUid: {},
        list: [],
        meta: { header: section.header, ...section.meta }// meta collisions are undefined.
      }
    }
    const objects = rowsToObjects(section)
    const _section = sections[sk]
    Object.assign(_section.byUid, objects)
  })

  for (const section of Object.values(sections)) {
    section.list = Object.values(section.byUid)
  }

  return sections
}

function applyMeta (sections: SectionDict) {
  for (const section of Object.values(sections)) {
    const meta = new Meta(section.meta)
    // Foreign keys
    for (const relation of meta.foreign_keys) {
      relation.apply(section, sections)
    }

    // through tables
    if (meta.through) {
      meta.through.apply(section, sections)
    }

    // transformations
    (section.meta.transformations ?? [] as const)
      .filter((t) => dataTransformations.verify(t))
      .forEach((t) =>
        section.list.forEach((item) => dataTransformations.apply(t, item))
      )
  }

  return sections
}

function fixAnnoyingIssues (data: any) {
  // Keeping speakers and speaker periods synched is harder than cleaning up client-side
  // TODO this is probably fixble with the 'through' meta attributes, but also see the workaround for through tables in congress.signals.
  if (!data.SpeakerPeriod || !data.Speaker) {
    return
  }
  Object.keys(data?.SpeakerPeriod.byUid ?? {}).forEach((uid) => {
    const period = data.SpeakerPeriod.byUid[uid]
    if (!(period.speaker__uid in data.Speaker.byUid)) {
      delete data.SpeakerPeriod.byUid[uid]
    }
  })
}

type State = UpdateData<any>

const connectionData: Record<string, State> = reactive({})
console.debug("connectionData", connectionData)
let init = false

export function mapUpdate<Sections = any>(updateData: UpdateSourceData) {
  const { data: routerData } = updateData

  // process incoming data into { sectionKey: section }
  const sections = mapSectionData(routerData) as SectionDict<Sections>
  applyMeta(sections)
  fixAnnoyingIssues(sections)

  return sections
}

function applyUpdate(updateData: UpdateSourceData) {
  const { path, meta, raw, data: routerData } = updateData
  const data = connectionData[path]

  if (!data) {
    console.error("Apply update called unknown path", path)
    return
  }

  const sections = mapUpdate(updateData)
  // #TODO find a way to merge this in, so that watchers can watch deeper properties
  Object.assign(data, { sections, raw, updateId: data.updateId + 1})
  Object.assign(data.meta, meta)
  Object.assign(data.static, routerData['#static'])
}

const _listenerRegistrationInitData = new WeakSet<WorkerConnection>()
export function useWebsocketUpdates() {
  const { workerConnection: wc } = useWorkerConnection()

  if (!_listenerRegistrationInitData.has(wc)) {
    _listenerRegistrationInitData.add(wc)
  }

  return {
    registerPath: (path: string) => _registerPath(wc, path),
    unregisterPath: (path: string) => _unregisterPath(wc, path),
  }
}

export type PluginOptions = MaybeRef<{
  sharedWorker: URL,
  dedicatedWorker: URL,
}>

export const WebsocketUpdatePlugin: Plugin<PluginOptions> = {
  install (app, options) {
    // provide an init function for NestedEntrypoint
    const { workerConnection: wc } = provideWorkerConnection(app)
    wc.addEventListener('update', ((event: CustomEvent<UpdateSourceData>) => applyUpdate(event.detail)) as EventListener)
  }
}

function _registerPath<Data extends UpdateData<any>>(wc: WorkerConnection, path: string) {
  if (!(path in connectionData)) {
    connectionData[path] = {
      updateId: -1,
      meta: {
        health: 'pending',
        connected: false,
        initialized: false,
        synchronizing: false,
        cachedData: false,
        dataVersion: '-',
        lastResponseCode: undefined,
      },
      sections: {},
      raw: {},
      static: {},
    }
    console.debug("Registered new connection path", path)
  }

  const data = connectionData[path]
  wc.registerPath(path)

  return data as Data
}

export function _unregisterPath(wc: WorkerConnection, path: string) {
  const data = connectionData[path]
  if (!data) {
    console.error("no connection state found for path", path)
    return
  }

  const empty = !wc.unregisterPath(path)
  if (empty) {
    delete connectionData[path]
  }
}
